import {
  AddRolesParams,
  AddRolesPermissionsParams,
  AddRolesSubjectsParams,
  CheckRolePermissionsParams,
  CheckRolePermissionsResponse,
  CompletePermission,
  DeleteRolePermissionsParams,
  DeleteRolesParams,
  DeleteRolesSubjectsParams,
  DeleteUserRolesResponse,
  EditRoleParams,
  GetPermissionsResponse,
  GetPlatformPermissionsParams,
  GetRoleParams,
  GetRolePermissionsParams,
  GetRolePermissionsResponse,
  GetRolesParams,
  GetRolesResponse,
  GetRolesSubjectsParams,
  GetSubjectsWithPermissionParams,
  GetSubjectsWithPermissionResponse,
  GetUserPermissionsParams,
  RbacApi,
  RoleEntity,
  RolePermission,
  RoleSubjectEntity,
  SyncUserScopesParams,
  SyncUserScopesResult,
  UpdateRolePermissionsResponse,
  UpdateRolesPermissionsParams,
} from '@agilelab/plugin-wb-rbac-common';
import { ResponseError } from '@backstage/errors';
import { DiscoveryApi } from '@backstage/plugin-permission-common';
import fetch from 'cross-fetch';

type SendRequestOptions = {
  path: string;
  method: 'POST' | 'GET' | 'DELETE' | 'PUT';
  body?: BodyInit | null | undefined;
  token?: string;
  headers?: Record<string, string>;
};

export class RbacClient implements RbacApi {
  constructor(private readonly discoveryApi: DiscoveryApi) {}

  private composeQueryString(queryObject?: any): URLSearchParams {
    const queryString = new URLSearchParams();

    if (!queryObject) {
      return queryString;
    }
    Object.keys(queryObject).forEach(key => {
      if (queryObject[key]) {
        queryString.append(key, queryObject[key] as string);
      }
    });

    return queryString;
  }

  private async sendRequest<T>({
    path,
    method,
    token,
    body,
    headers = {},
  }: SendRequestOptions): Promise<T> {
    const baseUrl = `${await this.discoveryApi.getBaseUrl('rbac')}/`;
    const url = new URL(path, baseUrl);

    const response = await fetch(url.toString(), {
      method: method,
      headers: token
        ? {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...headers,
          }
        : {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...headers,
          },
      body: body,
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json() as Promise<T>;
  }

  async getAvailableEditors({
    options,
  }: {
    options: { token?: string };
  }): Promise<number> {
    const updateResponse = await this.sendRequest<{
      result: number;
    }>({
      path: `available-editors`,
      method: 'GET',
      token: options?.token,
    });

    return updateResponse.result;
  }

  async syncUserScopes({
    syncData,
    options,
  }: SyncUserScopesParams): Promise<SyncUserScopesResult> {
    const updateResponse = await this.sendRequest<{
      result: SyncUserScopesResult;
    }>({
      path: `roles-subjects`,
      method: 'PUT',
      body: JSON.stringify({ syncData, options }),
      token: options?.token,
    });

    return updateResponse.result;
  }

  async addRolesSubjects({
    rolesSubjects,
    options,
  }: AddRolesSubjectsParams): Promise<RoleSubjectEntity[]> {
    const addResponse = await this.sendRequest<{
      rolesSubjects: RoleSubjectEntity[];
    }>({
      path: `roles-subjects`,
      method: 'POST',
      body: JSON.stringify({ rolesSubjects, options }),
      token: options?.token,
      headers: options?.xUserRef
        ? { 'X-User-Ref': options.xUserRef }
        : undefined,
    });

    return addResponse.rolesSubjects;
  }

  async deleteRolesSubjects({
    filters,
    options,
  }: DeleteRolesSubjectsParams): Promise<RoleSubjectEntity[]> {
    const queryString = this.composeQueryString(filters);

    const deleteResponse = await this.sendRequest<{
      rolesSubjects: RoleSubjectEntity[];
    }>({
      path: `roles-subjects?${queryString}`,
      method: 'DELETE',
      token: options?.token,
    });

    return deleteResponse.rolesSubjects;
  }

  async getRolesSubjects({
    searchKeyword,
    filters,
    options,
  }: GetRolesSubjectsParams): Promise<RoleSubjectEntity[]> {
    const queryString = this.composeQueryString(filters);

    if (searchKeyword) {
      queryString.append('searchKeyword', searchKeyword);
    }

    const getResponse = await this.sendRequest<{
      rolesSubjects: RoleSubjectEntity[];
    }>({
      path: `roles-subjects?${queryString}`,
      method: 'GET',
      token: options?.token,
    });

    return getResponse.rolesSubjects;
  }

  async getRolePermissions({
    searchKeyword,
    roleId,
    options,
  }: GetRolePermissionsParams): Promise<GetRolePermissionsResponse> {
    const queryString = new URLSearchParams();

    if (roleId) {
      queryString.append('roleId', roleId);
    }

    if (searchKeyword) {
      queryString.append('searchKeyword', searchKeyword);
    }

    if (
      options &&
      options.limit !== undefined &&
      options.offset !== undefined
    ) {
      queryString.append('limit', String(options.limit));
      queryString.append('offset', String(options.offset));
    }

    const getResponse = await this.sendRequest<GetRolePermissionsResponse>({
      path: `role-permissions?${queryString}`,
      method: 'GET',
      token: options?.token,
    });

    return getResponse;
  }

  async getRole({ roleId, options }: GetRoleParams): Promise<RoleEntity> {
    const queryString = new URLSearchParams();
    if (roleId) {
      queryString.append('roleId', roleId);
    }

    const result = await this.sendRequest<RoleEntity>({
      path: `role?${queryString}`,
      method: 'GET',
      token: options?.token,
    });

    return result;
  }

  async getRoles({
    searchKeyword,
    options,
  }: GetRolesParams): Promise<GetRolesResponse> {
    const queryString = new URLSearchParams();
    if (searchKeyword) {
      queryString.append('searchKeyword', searchKeyword);
    }

    if (
      options &&
      options.limit !== undefined &&
      options.offset !== undefined
    ) {
      queryString.append('limit', String(options.limit));
      queryString.append('offset', String(options.offset));
    }

    if (options && options.visibility) {
      queryString.append('visibility', options.visibility);
    }

    const result = await this.sendRequest<GetRolesResponse>({
      path: `roles?${queryString}`,
      method: 'GET',
      token: options?.token,
    });

    return result;
  }

  async getUserPermissions({
    options,
  }: GetUserPermissionsParams): Promise<Array<CompletePermission>> {
    const queryString = new URLSearchParams();

    if (options && options.permissionId) {
      queryString.append('permissionId', options.permissionId);
    }

    const { userPermissions } = await this.sendRequest<{
      userPermissions: CompletePermission[];
    }>({
      path: `permissions?${queryString}`,
      method: 'GET',
      token: options?.token,
    });

    return userPermissions;
  }

  async getPlatformPermissions({
    searchKeyword,
    options,
  }: GetPlatformPermissionsParams): Promise<GetPermissionsResponse> {
    const queryString = new URLSearchParams();

    if (options && options.permissionId) {
      queryString.append('permissionId', options.permissionId);
    }

    if (searchKeyword) {
      queryString.append('searchKeyword', searchKeyword);
    }

    if (
      options &&
      options.limit !== undefined &&
      options.offset !== undefined
    ) {
      queryString.append('limit', String(options.limit));
      queryString.append('offset', String(options.offset));
    }

    const permissions = await this.sendRequest<GetPermissionsResponse>({
      path: `platform-permissions?${queryString}`,
      method: 'GET',
      token: options?.token,
    });

    return permissions;
  }

  async addRoles({
    roles,
    options,
  }: AddRolesParams): Promise<Array<RoleEntity>> {
    const addResponse = await this.sendRequest<{
      roles: RoleEntity[];
    }>({
      path: `roles`,
      method: 'POST',
      body: JSON.stringify({ roles, options }),
      token: options?.token,
    });

    return addResponse.roles;
  }

  async editRole({ role, options }: EditRoleParams): Promise<RoleEntity> {
    const editResponse = await this.sendRequest<{
      role: RoleEntity;
    }>({
      path: `role`,
      method: 'PUT',
      body: JSON.stringify({ role, options }),
      token: options?.token,
    });

    return editResponse.role;
  }

  async deleteUserRoles({
    roles,
    options,
  }: DeleteRolesParams): Promise<DeleteUserRolesResponse> {
    const deleteResponse = await this.sendRequest<DeleteUserRolesResponse>({
      path: `user-roles`,
      method: 'DELETE',
      body: JSON.stringify({
        roles,
      }),
      token: options?.token,
    });

    return deleteResponse;
  }

  async addRolesPermissions({
    rolesPermissions,
    options,
  }: AddRolesPermissionsParams): Promise<Array<RolePermission>> {
    const addResponse = await this.sendRequest<{
      roles: RolePermission[];
    }>({
      path: `roles-permissions`,
      method: 'POST',
      body: JSON.stringify({ rolesPermissions, options }),
      token: options?.token,
    });

    return addResponse.roles;
  }

  async deleteRolePermissions({
    filters,
    options,
  }: DeleteRolePermissionsParams): Promise<Array<RolePermission>> {
    const queryString = this.composeQueryString(filters);

    const deleteResponse = await this.sendRequest<{
      rolePermissions: RolePermission[];
    }>({
      path: `roles-permissions?${queryString}`,
      method: 'DELETE',
      token: options?.token,
    });

    return deleteResponse.rolePermissions;
  }

  async updateRolePermissions({
    roleId,
    permissions,
    options,
  }: UpdateRolesPermissionsParams): Promise<UpdateRolePermissionsResponse> {
    const updateResponse =
      await this.sendRequest<UpdateRolePermissionsResponse>({
        path: `roles-permissions`,
        method: 'PUT',
        body: JSON.stringify({ roleId, permissions }),
        token: options?.token,
      });

    return updateResponse;
  }

  async checkRolePermissions({
    checks,
    options,
  }: CheckRolePermissionsParams): Promise<CheckRolePermissionsResponse> {
    const checkResponse = await this.sendRequest<CheckRolePermissionsResponse>({
      path: `roles/check-permissions`,
      method: 'POST',
      body: JSON.stringify({ checks }),
      token: options?.token,
    });

    return checkResponse;
  }

  async getSubjectsWithPermission({
    permissionId,
    roleIds,
    subjectKind,
    scope,
    excludeWildcard,
    options,
  }: GetSubjectsWithPermissionParams): Promise<GetSubjectsWithPermissionResponse> {
    const queryString = new URLSearchParams();

    if (permissionId) {
      queryString.append('permissionId', permissionId);
    }

    if (roleIds && roleIds.length > 0) {
      queryString.append('roleIds', roleIds.join(','));
    }

    if (subjectKind) {
      queryString.append('subjectKind', subjectKind);
    }

    if (scope) {
      queryString.append('scope', scope);
    }

    if (excludeWildcard !== undefined) {
      queryString.append('excludeWildcard', String(excludeWildcard));
    }

    const getResponse =
      await this.sendRequest<GetSubjectsWithPermissionResponse>({
        path: `subjects-with-permission?${queryString}`,
        method: 'GET',
        token: options?.token,
      });

    return getResponse;
  }
}
